/**
 * 📱 App-install referral helper.
 *
 * Lightweight, non-disruptive. Used by the "Get the App" banner today;
 * ready to be consumed by the Expo app (Phase 2) via the Google Play
 * Install Referrer API.
 *
 *   web side
 *   --------
 *   • `pathToRef(pathname)` — maps any web path to a coarse `ref` slug
 *     (e.g. `/magazine/2025-q1` → `magazine`, `/artist/abc` → `artist`).
 *   • `saveAppRef({ ref, path })` — persists the latest source for 30 days
 *     in `localStorage`, so the Expo app can also read it post-install if
 *     the user happens to log in to the same browser session afterwards.
 *   • `buildPlayStoreUrl(baseUrl, { ref, path })` — appends a Play
 *     Install Referrer (`&referrer=`) carrying ref+path. Google Play
 *     forwards this verbatim to the installed app via the
 *     Install Referrer API, where the Expo app can decode it later.
 *
 *   mobile side (Phase 2 — out of scope here)
 *   ----------
 *   The Expo app would call `getInstallReferrer()` from
 *   `react-native-play-install-referrer` once on first launch and parse
 *   the `ref` / `path` keys to deep-link the user to the right screen.
 *
 * Everything is best-effort and silent on failure — if storage is
 * unavailable, the banner still works; if no ref is computable, the
 * Play Store URL is opened unchanged.
 */

const STORAGE_KEY = "rgm_app_ref_v1";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Map a pathname to a coarse-grained source label.
 * Keep this small and stable — the mobile app reads the same enum.
 */
export const pathToRef = (pathname = "/") => {
  const p = String(pathname || "/").toLowerCase();
  if (p === "/" || p === "") return "home";
  if (p.startsWith("/magazine")) return "magazine";
  if (p.startsWith("/artist")) return "artist";
  if (p.startsWith("/track")) return "track";
  if (p.startsWith("/charts")) return "charts";
  if (p.startsWith("/explore") || p.startsWith("/discover")) return "explore";
  if (p.startsWith("/live") || p.startsWith("/beams")) return "live";
  if (p.startsWith("/community") || p.startsWith("/crews")) return "community";
  if (p.startsWith("/spotlight") || p.startsWith("/leaderboard")) return "spotlight";
  if (p.startsWith("/business") || p.startsWith("/hub")) return "hub";
  if (p.startsWith("/support") || p.startsWith("/help")) return "support";
  if (p.startsWith("/login") || p.startsWith("/signup") || p.startsWith("/auth")) return "auth";
  return "other";
};

/**
 * Persist {ref, path, ts} for 30 days.
 * Safe on private-mode / disabled storage — silently no-ops.
 */
export const saveAppRef = ({ ref, path }) => {
  try {
    const payload = {
      ref: ref || "other",
      path: (path || "/").slice(0, 200),
      ts: Date.now(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    return payload;
  } catch {
    return null;
  }
};

/**
 * Load the most recent stored ref. Returns null if missing, malformed,
 * or expired beyond TTL_MS.
 */
export const loadAppRef = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.ts || Date.now() - parsed.ts > TTL_MS) {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch {}
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Append a Play Install Referrer to a Google Play URL.
 *
 *   in:  https://play.google.com/store/apps/details?id=com.rgmstudio.app
 *   out: ...&referrer=utm_source%3Drgm_web%26utm_medium%3Dapp_banner%26ref%3Dmagazine%26path%3D%2Fmagazine
 *
 * If the input doesn't look like a Play Store URL we return it unchanged
 * (e.g. a short link or alternate listing) — never break the user's CTA.
 */
export const buildPlayStoreUrl = (baseUrl, { ref, path, medium = "app_banner" } = {}) => {
  if (!baseUrl) return baseUrl;
  try {
    const url = new URL(baseUrl);
    const host = url.hostname.toLowerCase();
    // Only inject referrer for the Play Store domain — leaves short links
    // / opt-in URLs untouched.
    if (!host.endsWith("play.google.com")) return baseUrl;

    const referrerParts = [
      `utm_source=rgm_web`,
      `utm_medium=${medium}`,
      `utm_campaign=get_the_app`,
    ];
    if (ref)  referrerParts.push(`ref=${encodeURIComponent(ref)}`);
    if (path) referrerParts.push(`path=${encodeURIComponent(path)}`);
    // Note: encodeURIComponent on the joined string preserves "&" as %26
    // which is what Play Install Referrer expects.
    url.searchParams.set("referrer", referrerParts.join("&"));
    return url.toString();
  } catch {
    return baseUrl;
  }
};

export const _internal = { STORAGE_KEY, TTL_MS };
